import { useTranslation } from 'react-i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import Growth from '@/assets/icons/growth.svg?component';
import LivingWeight from '@/assets/icons/living_weight.svg?component';
import Target from '@/assets/icons/target.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import { MetricCard } from '@/components/molecules/MetricCard';
import type { DomainBiomassDay, DomainCageOverviewPeriod } from '@/domain/cages/overview.types';
import WeightAndGrowthSummary from '@/routes/Client/Cage/components/WeightAndGrowthSummary';
import { OverviewChartCard, useOverviewChartSpecs } from '@/routes/Client/Cage/overviewCharts';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatNumberByLocale } from '@/utils/localeFormatters';

const CHART_HEIGHT = 400;
const INFO_COLUMN_WIDTH = 375;
const ARROW_ICON_SIZE = 20;
const GROWTH_DECIMALS = 1;
const WEIGHT_DECIMALS = 0;
const NO_VALUE = '-';

export interface CageWeightDashboardViewProps {
    days: DomainBiomassDay[];
    /** Dayparts that get a series, from `filterGraphDayparts`. */
    graphedDaypartTags: string[];
    /** Dayparts that start visible. */
    activeDaypartTags: string[];
    /** The single daypart the charts that do not fan out should plot. */
    daypartTag: string;
    weightUnitTag: string;
    /** Living weight on the last measurement day, in `weightUnitTag`. */
    estimatedWeight: number | null;
    period: DomainCageOverviewPeriod;
    isLoading: boolean;
    error?: unknown;
    /** Fetches the overview again from the chart's error state. */
    onRetry?: () => void;
    isRetrying?: boolean;
    dateFrom?: string;
    dateTo?: string;
    onDateChange: (dates: DateRange) => void;
    /** Navigates to the weight graphs page. */
    onOpenGraphs: () => void;
}

export default function CageWeightDashboardView(props: CageWeightDashboardViewProps) {
    const {
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
        estimatedWeight,
        period,
        isLoading,
        error,
        onRetry,
        isRetrying,
        dateFrom,
        dateTo,
        onDateChange,
        onOpenGraphs,
    } = props;
    const { t: tCharts } = useTranslation('cageCharts');
    const { t, i18n } = useTranslation();

    const specs = useOverviewChartSpecs({
        days,
        graphedDaypartTags,
        activeDaypartTags,
        daypartTag,
        weightUnitTag,
    });

    const livingWeight = formatNumberByLocale(
        estimatedWeight,
        i18n.language,
        WEIGHT_DECIMALS,
        NO_VALUE
    );
    const growthUnit = `${t(`weightUnitTag.${weightUnitTag}`)}${t('common.labels.perDay')}`;

    return (
        <GridLayout contentLayout="1">
            <GridLayout contentLayout="3-fluid">
                <ContentBlock spacing="none">
                    <Tooltip content={t('common.labels.estimatedLivingWeight')}>
                        <MetricCard
                            title={t('common.labels.livingWeight')}
                            value={livingWeight}
                            suffix={weightUnitTag}
                            icon={LivingWeight}
                            tone="caution"
                            isLoading={isLoading}
                        />
                    </Tooltip>
                </ContentBlock>
                <ContentBlock spacing="none">
                    <Tooltip content={t('descriptions.weightAndGrowthGrowth')}>
                        <MetricCard
                            title={t('common.labels.growth')}
                            value={formatNumberByLocale(
                                period.weightGrowth,
                                i18n.language,
                                GROWTH_DECIMALS,
                                NO_VALUE
                            )}
                            suffix={growthUnit}
                            icon={Growth}
                            tone="info"
                            isLoading={isLoading}
                        />
                    </Tooltip>
                </ContentBlock>
                <ContentBlock spacing="none">
                    {/* No value yet: the feed conversion ratio is only charted on the
                        graphs page. The card is kept so the row keeps its shape. */}
                    <MetricCard
                        title={t('cageWeight.dashboard.bFCR')}
                        value={NO_VALUE}
                        icon={Target}
                        tone="success"
                        isLoading={isLoading}
                    />
                </ContentBlock>
            </GridLayout>
            <GridLayout contentLayout="1-fixed" secondColumnWidth={INFO_COLUMN_WIDTH}>
                <OverviewChartCard
                    title={tCharts('title.livingWeight')}
                    chartName="cage-living-weight"
                    csvExport={false}
                    spec={specs.livingWeight}
                    chartHeight={CHART_HEIGHT}
                    isLoading={isLoading}
                    error={error}
                    onRetry={onRetry}
                    isRetrying={isRetrying}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                    contentBlockSpacing="none"
                />
                <ContentBlock
                    title={t('common.labels.weightAndGrowth')}
                    headerActions={
                        <Button
                            variant="secondary"
                            trackingId="weight_dashboard_open_graphs"
                            onClick={onOpenGraphs}
                            iconOnly
                            aria-label={t('common.actions.view')}
                            icon={
                                <SvgIcon
                                    color="currentColor"
                                    icon={ArrowRightLine}
                                    size="custom"
                                    customSize={ARROW_ICON_SIZE}
                                />
                            }
                        />
                    }>
                    <WeightAndGrowthSummary
                        period={period}
                        weightUnitTag={weightUnitTag}
                        isLoading={isLoading}
                    />
                </ContentBlock>
            </GridLayout>
        </GridLayout>
    );
}
